import {isAntiforgeryFailure, REFRESH_PAGE_MSG} from '../../../global/antiforgery';

export const DEFAULT_IMAGE_UPLOAD_ERROR = 'Error uploading image. Please try again or contact customer service.';

export type NaturalCropRect = {
    x: number;
    y: number;
    width: number;
    height: number;
    unit: 'px';
};

type CropSourceImage = {
    naturalWidth: number;
    naturalHeight: number;
    width: number;
    height: number;
};

export const pngNameFromPdf = (fileName: string): string => fileName.replace(/\.pdf$/i, '.png');

export const normalizeImageUploadError = (err: unknown, fallback: string = DEFAULT_IMAGE_UPLOAD_ERROR): string => {
    // The upload (posted during Add to Cart) and the PDF conversion: a stale token the retry could not fix needs a reload.
    if (isAntiforgeryFailure(err)) {
        return REFRESH_PAGE_MSG;
    }
    if (typeof err === 'string') {
        const trimmed = err.trim();
        return trimmed || fallback;
    }

    return fallback;
};

export const isBlobObjectUrl = (url: string | undefined | null): boolean => typeof url === 'string' && url.startsWith('blob:');

export const revokeBlobObjectUrl = (url: string | undefined | null): void => {
    if (isBlobObjectUrl(url)) {
        URL.revokeObjectURL(url as string);
    }
};

export const canvasToFile = (canvas: Pick<HTMLCanvasElement, 'toBlob'>, fileName: string, fileType: string): Promise<File | null> => {
    return new Promise((resolve, reject) => {
        try {
            canvas.toBlob(blob => {
                if (!blob) {
                    resolve(null);
                    return;
                }

                resolve(new File([blob], fileName, {type: fileType || blob.type}));
            });
        } catch (err) {
            reject(err);
        }
    });
};

export const renderCropToCanvas = (image: HTMLImageElement & CropSourceImage, cropData: {x: number; y: number; width: number; height: number}): {canvas: HTMLCanvasElement; naturalCrop: NaturalCropRect} | null => {
    const {naturalWidth, naturalHeight, width, height} = image;

    if (!naturalWidth || !naturalHeight || !width || !height || !cropData.width || !cropData.height) {
        return null;
    }

    const scaleX = naturalWidth / width;
    const scaleY = naturalHeight / height;
    const naturalCropWidth = Math.round(cropData.width * scaleX);
    const naturalCropHeight = Math.round(cropData.height * scaleY);

    if (naturalCropWidth < 1 || naturalCropHeight < 1) {
        return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = naturalCropWidth;
    canvas.height = naturalCropHeight;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return null;
    }

    ctx.drawImage(image, cropData.x * scaleX, cropData.y * scaleY, cropData.width * scaleX, cropData.height * scaleY, 0, 0, naturalCropWidth, naturalCropHeight);

    return {
        canvas,
        naturalCrop: {
            x: cropData.x * scaleX,
            y: cropData.y * scaleY,
            width: cropData.width * scaleX,
            height: cropData.height * scaleY,
            unit: 'px',
        },
    };
};
